/* eslint-disable array-bracket-newline */
import { useCallback } from 'react';
import { useEffect, useReducer } from 'react';
import { useBalance } from 'wagmi';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import { calculateUSDValue } from '../../../../../helpers/calculateUSDValue';
import { Direction, useMarketTrade, useTradeEstimate } from '../../../hooks';
import {
  swapReducer,
  initialSwapState,
  SwapActions,
} from '../../../reducers/swapReducer';
import { estimateSuccessCallbackType } from '../../../hooks/useTradeEstimate';
import { getPriceForPair } from '../../../helpers/statistics';
import { BASE_TOKEN } from '../../../helpers/nativeCoins';
import { ISwapProps } from '..';
import { truncateDecimals } from '../../../../../helpers/truncate';
import { getNetworkFlags } from '../../../../../wallet/helpers';

const useSwap = ({
  current,
  base,
  quote,
  meta,
  slippage,
  onSetBaseAmount,
  onSetQuoteAmount,
  onNotify,
  onWalletRefresh,
  dexChainId,
  chainId,
  account,
  ratesAddress,
}: ISwapProps) => {
  const [state, dispatch] = useReducer(swapReducer, initialSwapState);

  const network = Network.fromChain(current.chain);
  const { isEVM } = getNetworkFlags(chainId);
  let isSameChain = chainId === network?.toDecimal();
  if (!isSameChain) {
    if (network?.toDecimal() === 99999999) {
      // tron
      isSameChain = true;
    }
  }

  useBalance({
    address: account as any,
    token: base.address == BASE_TOKEN ? '' : (base.address as any),
    chainId: network?.toDecimal(),
    watch: true,
    enabled: isEVM && isSameChain,
    onSuccess(data) {
      const amount = new BigNumber(data.formatted);
      dispatch({
        type: SwapActions.SetBaseBalance,
        payload: truncateDecimals(amount.toNumber(), 4),
      });
      const baseUSDValue = calculateUSDValue(
        base.address,
        amount,
        ratesAddress
      );
      dispatch({
        type: SwapActions.SetBaseUSDBalance,
        payload: baseUSDValue,
      });
    },
  });

  useBalance({
    address: account as any,
    token: quote.address == BASE_TOKEN ? '' : (quote.address as any),
    chainId: network?.toDecimal(),
    watch: true,
    enabled: isEVM && isSameChain,
    onSuccess(data) {
      const amount = new BigNumber(data.formatted);
      dispatch({
        type: SwapActions.SetQuoteBalance,
        payload: truncateDecimals(amount.toNumber(), 4),
      });
      const quoteUSDValue = calculateUSDValue(
        quote.address,
        amount,
        ratesAddress
      );
      dispatch({
        type: SwapActions.SetQuoteUSDBalance,
        payload: quoteUSDValue,
      });
    },
  });

  const onEstimateError = useCallback(() => {
    dispatch({ payload: false, type: SwapActions.SetEstimated });
    dispatch({ payload: null, type: SwapActions.SetPriceRoute });
    dispatch({ payload: '0.0', type: SwapActions.SetGasCostUSD });
    dispatch({ payload: '0.0', type: SwapActions.SetMinReceived });
    if (state.direction == Direction.QuoteBase) {
      onSetBaseAmount(new BigNumber(0));
    } else {
      onSetQuoteAmount(new BigNumber(0));
    }
  }, [onSetBaseAmount, onSetQuoteAmount, state.direction]);

  const onEstimateSuccess = useCallback(
    ({
      gasCostUSD,
      naturalAmount,
      afterSlippage,
      priceRoute,
      quoteNaturalAmount,
    }: estimateSuccessCallbackType) => {
      dispatch({ payload: gasCostUSD, type: SwapActions.SetGasCostUSD });
      dispatch({ payload: priceRoute, type: SwapActions.SetPriceRoute });
      dispatch({ payload: true, type: SwapActions.SetEstimated });
      dispatch({
        payload: Number((Date.now() / 1000).toFixed()),
        type: SwapActions.SetLastUpdated,
      });
      if (state.direction == Direction.QuoteBase && quoteNaturalAmount) {
        onSetBaseAmount(naturalAmount);
        dispatch({
          payload: quoteNaturalAmount.times(afterSlippage).toFixed(6),
          type: SwapActions.SetMinReceived,
        });
      } else if (state.direction == Direction.BaseQuote) {
        onSetQuoteAmount(naturalAmount);
        dispatch({
          payload: naturalAmount.times(afterSlippage).toFixed(6),
          type: SwapActions.SetMinReceived,
        });
      }
    },
    [onSetBaseAmount, onSetQuoteAmount, state.direction]
  );

  const onEstimateStart = (base: boolean) => {
    if (base) {
      dispatch({ type: SwapActions.ToggleEstimationBase });
    } else {
      dispatch({ type: SwapActions.ToggleEstimationQuote });
    }
  };

  const { rate, gasPrice } = useTradeEstimate({
    network: dexChainId || chainId,
    base,
    quote,
    direction: state.direction,
    slippage,
    estimateIndex: state.estimateIndex,
    priceRoute: state.priceRoute,
    isOnEVMNetwork: isEVM,
    onNotify,
    onEstimateStart,
    onEstimateError,
    onEstimateSuccess,
  });

  const { loading, onSwap, swapText } = useMarketTrade({
    base,
    quote,
    network: dexChainId || chainId,
    userAddress: account,
    slippage,
    priceRoute: state.priceRoute,
    estimated: state.estimated,
    onNotify,
    onWalletRefresh,
  });

  // re-estimate every 7 second
  useEffect(() => {
    if (state.estimated) {
      const timer = setTimeout(() => {
        dispatch({
          payload: state.estimateIndex + 1,
          type: SwapActions.SetEstimateIndex,
        });
      }, 7000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [state.estimated, state.lastUpdated]);

  /**
   * Do estimate call to fetch quote value
   */
  const onBaseUpdate = useCallback(
    (input: number) => {
      if (state.direction != Direction.BaseQuote) {
        dispatch({
          payload: Direction.BaseQuote,
          type: SwapActions.SetDirection,
        });
      }

      const newInput = new BigNumber(input);
      if (!base.amount.eq(newInput)) {
        onSetBaseAmount(newInput);
      }
    },
    [onSetBaseAmount, base.amount, state.direction]
  );

  /**
   * Do estimate call to fetch base value
   */
  const onQuoteUpdate = useCallback(
    (input: number) => {
      if (state.direction != Direction.QuoteBase) {
        dispatch({
          payload: Direction.QuoteBase,
          type: SwapActions.SetDirection,
        });
      }
      const newInput = new BigNumber(input);
      if (!quote.amount.eq(newInput)) {
        onSetQuoteAmount(new BigNumber(input));
      }
    },
    [onSetQuoteAmount, quote.amount, state.direction]
  );

  const mergedState = {
    ...state,
    rate,
    loading,
    swapText,
    network,
    isSameChain,
    price: getPriceForPair({ ticker: meta, rate }),
    gasPrice,
    onSwap,
    dispatch,
    onBaseUpdate,
    onQuoteUpdate,
  };

  return mergedState;
};

export default useSwap;
